import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const useBlocker = (blocker, when) => {
  const navigate = useNavigate();
  const location = useLocation();

  // NOTE: Removed beforeunload to avoid native browser alert.
  // This hook now only provides a programmatic signal; caller must handle UI modal.
  const activeRef = useRef(false);

  useEffect(() => {
    if (!when) {
      activeRef.current = false;
      return;
    }
    activeRef.current = true;
    const handlePopState = (e) => {
      if (!activeRef.current) return;
      e.preventDefault();
      blocker(true); // signal blocked; consumer should open modal
      // Push state again to negate back navigation
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [when, blocker]);
};

/*
 * Drop-in replacement for react-router-dom's useBlocker that works under a
 * plain <BrowserRouter> (no data router required). Returns the same shape:
 *   { state: "idle" | "blocked", proceed(), reset() }
 *
 * - Blocks browser back/forward (POP) via popstate interception.
 * - Blocks in-app <a>/<Link> clicks via a capture-phase click listener.
 * - `when`  : boolean, guard is active only while true.
 * - `allow` : optional (pathname) => boolean for paths that may pass freely.
 */
export const useAppBlocker = (when, allow) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("idle");

  const whenRef = useRef(when);
  const allowRef = useRef(allow);
  const guardedHrefRef = useRef(null);
  const pendingRef = useRef(null);
  const ignoreNextPopRef = useRef(false);
  const historyGuardActiveRef = useRef(false);
  const blockedRef = useRef(false);

  whenRef.current = when;
  allowRef.current = allow;

  useEffect(() => {
    if (!when) {
      guardedHrefRef.current = null;
      pendingRef.current = null;
      blockedRef.current = false;
      setState("idle");
    }
  }, [when]);

  useEffect(() => {
    if (!when) {
      historyGuardActiveRef.current = false;
      blockedRef.current = false;
      return;
    }

    if (!guardedHrefRef.current) {
      guardedHrefRef.current =
        window.location.pathname + window.location.search + window.location.hash;
    }

    // Add a same-route entry so browser back and touch swipe produce a
    // popstate event that can be confirmed before leaving the page.
    if (!historyGuardActiveRef.current) {
      window.history.pushState({ moduleNavigationGuard: true }, "", guardedHrefRef.current);
      historyGuardActiveRef.current = true;
    }

    /* POP navigation (browser back / forward / swipe) */
    const onPopState = () => {
      if (!whenRef.current) return;
      if (blockedRef.current) return;

      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }

      if (
        allowRef.current &&
        allowRef.current(window.location.pathname)
      ) {
        guardedHrefRef.current = null; // allowed to leave
        return;
      }

      // Block: restore both history entry and router state, then show modal.
      // The first back removed the guard entry; restore it now. On confirm,
      // go back across both the restored guard entry and the current route.
      pendingRef.current = () => window.history.go(-2);
      blockedRef.current = true;
      window.history.pushState(null, "", guardedHrefRef.current);
      navigate(guardedHrefRef.current, { replace: true });
      setState("blocked");
    };

    /* In-app link navigation (<a>, <Link>) */
    const onClickCapture = (e) => {
      if (!whenRef.current || !guardedHrefRef.current) return;
      if (e.defaultPrevented) return;
      const anchor =
        e.target && e.target.closest ? e.target.closest("a[href]") : null;
      if (!anchor) return;

      let href;
      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        href = url.pathname + url.search + url.hash;
      } catch {
        return;
      }
      if (href === guardedHrefRef.current) return;
      if (allowRef.current && allowRef.current(new URL(href, window.location.href).pathname)) return;

      e.preventDefault();
      e.stopPropagation();
      pendingRef.current = () => navigate(href);
      setState("blocked");
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [when, navigate, location.key]);

  const proceed = useCallback(() => {
    setState("idle");
    blockedRef.current = false;
    const pending = pendingRef.current;
    pendingRef.current = null;
    guardedHrefRef.current = null;
    historyGuardActiveRef.current = false;
    if (pending) {
      ignoreNextPopRef.current = true;
      pending();
    }
  }, []);

  const reset = useCallback(() => {
    setState("idle");
    blockedRef.current = false;
    pendingRef.current = null;
  }, []);

  return { state, proceed, reset };
};
